import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const COOLDOWN_KEY = 'pwa-install-dismissed-at';
const COOLDOWN_DAYS = 7;

function isInCooldown(): boolean {
  const dismissedAt = localStorage.getItem(COOLDOWN_KEY);
  if (!dismissedAt) return false;
  const elapsed = Date.now() - parseInt(dismissedAt, 10);
  return elapsed < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in window.navigator && (window.navigator as Record<string, unknown>).standalone === true);
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

interface UsePWAInstallReturn {
  canInstall: boolean;
  isIOSDevice: boolean;
  promptInstall: () => void;
  dismiss: () => void;
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOSDevice] = useState(() => isIOS() && !isStandalone() && !isInCooldown());

  useEffect(() => {
    if (isStandalone() || isInCooldown()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const promptInstall = useCallback(() => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choice) => {
      if (choice.outcome === 'accepted') {
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    });
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
    setCanInstall(false);
  }, []);

  return { canInstall, isIOSDevice, promptInstall, dismiss };
}
